// Overflow menu for actions that must not sit in the main button row —
// chiefly Archive, Deactivate and Delete.
//
// Ported from Zanetto Tools (phase 7 stage 2). Keeping permanent deletion one
// deliberate extra click away, rather than beside Save, is the whole point: the
// header row wraps on narrow screens, so an inline delete button has no stable
// position to build muscle memory against — and the thing you hit by accident
// on a wrapped row is the one that cannot be undone.
//
// The open menu is position:FIXED at measured viewport coordinates, not
// absolute inside the trigger's wrapper. Most of these menus live in a table
// row inside `.table-wrap`, which is overflow-x:auto — and the spec makes
// overflow-y auto with it, so an absolutely positioned menu could never
// overlay the page: on a short table (Subcontractors, Suppliers, a new
// company's Forms list) opening it grew the wrap a scrollbar and shoved the
// table around instead. A fixed box escapes every ancestor's clip. The price
// is that it does not follow a scroll, so any scroll while open simply closes
// the menu — the same answer native selects give.
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';

export interface ActionItem {
  label: string;
  onClick: () => void;
  tone?: 'danger' | 'warning';
  disabled?: boolean;
  hidden?: boolean;
}

export function ActionMenu({
  items,
  label = 'More actions',
  align = 'right',
}: {
  items: (ActionItem | null | false | undefined)[];
  label?: string;
  align?: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);
  /**
   * Viewport coordinates for the fixed menu, measured from the trigger once
   * the menu has rendered (its height decides whether it opens downward or
   * flips up). Null until measured; the menu renders invisibly for that one
   * pre-paint pass so the measurement has a real box to read.
   */
  const [pos, setPos] = useState<CSSProperties | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const visible = items.filter((i): i is ActionItem => Boolean(i) && !(i as ActionItem).hidden);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Where the fixed menu goes, measured before paint so it never flashes at
  // the wrong spot. Downward from the trigger by default; when the viewport
  // has no room below AND has room above, it flips up — a fixed menu hanging
  // past the bottom edge cannot be scrolled to, because scrolling closes it.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;
    const rect = trigger.getBoundingClientRect();
    const height = menu.offsetHeight;
    const width = menu.offsetWidth;
    const flipUp = rect.bottom + 4 + height > window.innerHeight && rect.top - 4 - height > 0;
    // Aligned to the trigger's edge, then CLAMPED to the viewport with an
    // 8px gutter: the menu is wider than a phone leaves beside the trigger,
    // and a right-aligned menu on a left-hand trigger (a card footer at
    // 375px) would otherwise hang off the screen — as the absolute version
    // always silently did.
    const aligned = align === 'right' ? rect.right - width : rect.left;
    const left = Math.min(Math.max(8, aligned), Math.max(8, window.innerWidth - width - 8));
    setPos({ top: flipUp ? rect.top - 4 - height : rect.bottom + 4, left });
  }, [open, align]);

  // A fixed box is anchored to the viewport, not the trigger, so once
  // anything scrolls the two part company. Close instead of chasing —
  // capture-phase, because the scroll that matters may happen inside a
  // nested container (`.table-wrap` itself) and those don't bubble.
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  // Move focus onto the first item when the menu opens, so the arrow keys have
  // somewhere to start. preventScroll matters now: focusing a box the browser
  // decides to scroll into view would fire the scroll-closes rule above and
  // shut the menu in the same breath it opened.
  useEffect(() => {
    if (open)
      menuRef.current
        ?.querySelector<HTMLButtonElement>('button:not([disabled])')
        ?.focus({ preventScroll: true });
  }, [open]);

  const moveFocus = (event: React.KeyboardEvent, delta: number) => {
    const buttons = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('button:not([disabled])') ?? []
    );
    if (buttons.length === 0) return;
    event.preventDefault();
    const i = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = (i + delta + buttons.length) % buttons.length;
    buttons[next]?.focus();
  };

  if (visible.length === 0) return null;

  // No `relative` on the wrapper any more — the menu is fixed to the
  // viewport, so the wrapper exists only to give the outside-click check one
  // node containing both the trigger and the menu.
  return (
    <div ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        className="btn-icon border border-gray-300 bg-white"
      >
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v.01M12 12v.01M12 19v.01" />
        </svg>
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label={label}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') moveFocus(event, 1);
            if (event.key === 'ArrowUp') moveFocus(event, -1);
          }}
          className="fixed z-30 min-w-[13rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
          style={pos ?? { top: 0, left: 0, visibility: 'hidden' }}
        >
          {visible.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
              className={`w-full px-3 py-2.5 text-left text-sm font-medium transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 max-sm:min-h-11 ${
                item.tone === 'danger'
                  ? 'text-red-700 hover:bg-red-50 focus:bg-red-50'
                  : item.tone === 'warning'
                    ? 'text-amber-700 hover:bg-amber-50 focus:bg-amber-50'
                    : 'text-gray-700 hover:bg-gray-50 focus:bg-gray-50'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
